import React, { useRef, useState } from "react";
import { Upload, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "./ui/Primitives";

/**
 * Unified import button. Accepts a list of file types and dispatches
 * to the correct importer based on file extension.
 *
 * Props:
 *   acceptCsvXlsx       : boolean — accept .csv .xlsx .xls
 *   acceptPdf           : boolean — accept .pdf
 *   onCsvXlsxFile(file) : called for csv/xlsx
 *   onPdfFile(file)     : called for pdf
 *   label, testId       : optional overrides
 */
export default function UnifiedImportButton({
  acceptCsvXlsx = true,
  acceptPdf = false,
  onCsvXlsxFile,
  onPdfFile,
  label = "Importieren",
  testId = "unified-import-btn",
  size = "md",
  variant = "secondary",
}) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);

  const accepts = [];
  if (acceptCsvXlsx) accepts.push(".csv", ".xlsx", ".xls");
  if (acceptPdf) accepts.push(".pdf");
  const accept = accepts.join(",");

  const hint = accepts
    .map((a) => a.replace(".", "").toUpperCase())
    .filter((v, i, arr) => arr.indexOf(v) === i)
    .join(", ");

  const onFile = async (file) => {
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    setBusy(true);
    try {
      if (["csv", "xlsx", "xls"].includes(ext)) {
        if (!acceptCsvXlsx || !onCsvXlsxFile) {
          toast.error(`Dieses Format wird nicht unterstützt. Bitte ${hint} hochladen.`);
          return;
        }
        await onCsvXlsxFile(file);
      } else if (ext === "pdf") {
        if (!acceptPdf || !onPdfFile) {
          toast.error(`Dieses Format wird nicht unterstützt. Bitte ${hint} hochladen.`);
          return;
        }
        await onPdfFile(file);
      } else {
        toast.error(`Dieses Format wird nicht unterstützt. Bitte ${hint} hochladen.`);
      }
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <span className="inline-flex items-center gap-2" data-testid={`${testId}-wrapper`}>
      <Button variant={variant} size={size} onClick={() => inputRef.current?.click()} disabled={busy} data-testid={testId} title={`${hint} werden unterstützt`}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {label}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0])}
        data-testid={`${testId}-input`}
      />
    </span>
  );
}
